import { useEffect, useRef, useState } from 'react';
import { useAuth, type UserRole } from '@/lib/auth/auth-context';
import { apiClient } from '@/lib/api/client';
import { Navigate, useLocation } from 'react-router-dom';
import { authRouteFor } from '@/pages/AuthCallbackPage';
import {
  Button,
  Column,
  Grid,
  Loading,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
  InlineNotification,
  ProgressBar,
} from '@carbon/react';
import {
  TrashCan, UserAdmin, ChartBar, Document, ConnectSource, Information,
  Ai, PlayFilled, CheckmarkFilled, ErrorFilled, Renew,
} from '@carbon/icons-react';
import { adminApi, type ReembedJob, type EmbeddingStats, type ReembedTarget } from '@/lib/api/admin.api';

interface UserListItem {
  id: string;
  email: string;
  display_name: string | null;
  avatar_url: string | null;
  role: UserRole;
  beta_allowed: boolean;
  beta_features_enabled: boolean;
  last_login_at: string | null;
  created_at: string;
}

/**
 * Per-user consumption stats returned by GET /api/auth/admin/stats.
 * Field names match the backend repository (`AdminUserStats`).
 */
interface UserConsumption {
  id: string;
  email: string;
  ontology_count: number;
  node_count: number;
  edge_count: number;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
}

/** Global totals returned by GET /api/auth/admin/stats. */
interface AdminTotals {
  user_count: number;
  ontology_count: number;
  node_count: number;
  edge_count: number;
  total_tokens: number;
  prompt_tokens: number;
  completion_tokens: number;
  event_count: number;
}

interface AdminStats {
  users: UserConsumption[];
  totals: AdminTotals;
}

/** Compact number formatter (1234 -> 1.2k) for the stats columns. */
const fmt = new Intl.NumberFormat(undefined, {
  notation: 'compact',
  maximumFractionDigits: 1,
});

/**
 * Admin page for managing users and their roles
 */
export function AdminPage() {
  const { isAdmin, isLoading: authLoading, isAuthenticated } = useAuth();
  const location = useLocation();
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; user: UserListItem | null }>({
    open: false,
    user: null,
  });

  // ── Embeddings panel state ─────────────────────────────────────────────────
  const [embedStats, setEmbedStats] = useState<EmbeddingStats | null>(null);
  const [embedStatsLoading, setEmbedStatsLoading] = useState(false);
  const [embedJobs, setEmbedJobs] = useState<ReembedJob[]>([]);
  const [embedError, setEmbedError] = useState<string | null>(null);
  // Track which target is currently launching (for button loading state)
  const [launching, setLaunching] = useState<ReembedTarget | null>(null);
  // Poll interval ref
  const embedPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (isAdmin) {
      void refresh();
      void refreshEmbedStats();
      void refreshEmbedJobs();
    }
  }, [isAdmin]);

  // Poll running jobs every 3 s
  useEffect(() => {
    const hasRunning = embedJobs.some((j) => j.status === 'running');
    if (hasRunning && !embedPollRef.current) {
      embedPollRef.current = setInterval(() => {
        void refreshEmbedJobs();
      }, 3000);
    } else if (!hasRunning && embedPollRef.current) {
      clearInterval(embedPollRef.current);
      embedPollRef.current = null;
      // Refresh stats once all jobs settled
      void refreshEmbedStats();
    }
    return () => {
      if (embedPollRef.current) {
        clearInterval(embedPollRef.current);
        embedPollRef.current = null;
      }
    };
  }, [embedJobs]);

  /**
   * Fetch users + admin stats in parallel. Stats failure is non-fatal —
   * the user table still renders even if the stats endpoint errors.
   */
  async function refreshEmbedStats() {
    setEmbedStatsLoading(true);
    try {
      const data = await adminApi.getEmbeddingStats();
      setEmbedStats(data);
    } catch (err: any) {
      console.warn('[AdminPage] Failed to fetch embedding stats:', err);
    } finally {
      setEmbedStatsLoading(false);
    }
  }

  async function refreshEmbedJobs() {
    try {
      const jobs = await adminApi.listReembedJobs();
      setEmbedJobs(jobs);
    } catch (err: any) {
      console.warn('[AdminPage] Failed to fetch re-embed jobs:', err);
    }
  }

  async function handleStartReembed(target: ReembedTarget) {
    setLaunching(target);
    setEmbedError(null);
    try {
      const job = await adminApi.startReembedJob(target);
      setEmbedJobs((prev) => [job, ...prev]);
    } catch (err: any) {
      setEmbedError(err.message || `Failed to start ${target} re-embedding job`);
    } finally {
      setLaunching(null);
    }
  }

  async function refresh() {
    try {
      setIsLoading(true);
      const [userData, statData] = await Promise.all([
        apiClient.get<UserListItem[]>('/api/auth/users'),
        apiClient
          .get<AdminStats>('/api/auth/admin/stats')
          .catch((err) => {
            console.warn('[AdminPage] Failed to fetch stats:', err);
            return null;
          }),
      ]);
      setUsers(userData);
      setStats(statData);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch users');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleDeleteUser() {
    if (!deleteModal.user) return;
    try {
      await apiClient.delete(`/api/auth/users/${deleteModal.user.id}`);
      setDeleteModal({ open: false, user: null });
      void refresh();
    } catch (err: any) {
      setError(err.message || 'Failed to delete user');
    }
  }

  /**
   * Grant or revoke beta access. Optimistically updates the row, then
   * reconciles by re-fetching. The backend will also force-disable the
   * user's `beta_features_enabled` when revoking, so we always refresh.
   */
  async function handleToggleBetaAllowed(user: UserListItem, allowed: boolean) {
    try {
      await apiClient.put(`/api/auth/users/${user.id}/beta-allowed`, {
        beta_allowed: allowed,
      });
      void refresh();
    } catch (err: any) {
      setError(err.message || 'Failed to update beta access');
    }
  }

  if (authLoading) {
    return (
      <Grid fullWidth>
        <Column lg={16} md={8} sm={4}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <Loading withOverlay={false} />
          </div>
        </Column>
      </Grid>
    );
  }

  if (!isAuthenticated) {
    // Carries the route, same reason as ProtectedRoute in App.tsx: a bare redirect to /auth loses where
    // the visitor was going, and the sign-in then lands them on the home page.
    return <Navigate to={authRouteFor(`${location.pathname}${location.search}${location.hash}`)} replace />;
  }

  if (!isAdmin) {
    return (
      <Grid fullWidth>
        <Column lg={16} md={8} sm={4}>
          <div style={{ padding: '2rem' }}>
            <InlineNotification
              kind="error"
              title="Access Denied"
              subtitle="You need admin privileges to access this page."
              hideCloseButton
            />
          </div>
        </Column>
      </Grid>
    );
  }

  const headers = [
    { key: 'email', header: 'Email' },
    { key: 'display_name', header: 'Name' },
    { key: 'role', header: 'Role' },
    { key: 'beta', header: 'Beta' },
    { key: 'ontologies', header: 'Ontologies' },
    { key: 'nodes', header: 'Nodes' },
    { key: 'edges', header: 'Edges' },
    { key: 'tokens', header: 'Tokens' },
    { key: 'last_login_at', header: 'Last Login' },
    { key: 'actions', header: 'Actions' },
  ];

  // Quick lookup for per-user consumption rows.
  const consumptionByUser = new Map<string, UserConsumption>(
    (stats?.users ?? []).map((u) => [u.id, u])
  );

  const emptyConsumption: UserConsumption = {
    id: '',
    email: '',
    ontology_count: 0,
    node_count: 0,
    edge_count: 0,
    prompt_tokens: 0,
    completion_tokens: 0,
    total_tokens: 0,
  };

  const rows = users.map((user) => {
    const c = consumptionByUser.get(user.id) ?? emptyConsumption;
    return {
      id: user.id,
      email: user.email,
      display_name: user.display_name || '—',
      role: user.role,
      ontologies: c.ontology_count,
      nodes: c.node_count,
      edges: c.edge_count,
      tokens: c.total_tokens,
      promptTokens: c.prompt_tokens,
      completionTokens: c.completion_tokens,
      last_login_at: user.last_login_at
        ? new Date(user.last_login_at).toLocaleDateString()
        : 'Never',
      actions: user,
    };
  });

  function getRoleTagType(role: UserRole): 'red' | 'blue' | 'gray' {
    switch (role) {
      case 'admin': return 'red';
      case 'editor': return 'blue';
      case 'viewer': return 'gray';
    }
  }

  return (
    <Grid fullWidth>
      <Column lg={16} md={8} sm={4}>
        <div style={{ padding: '2rem' }}>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 600, marginBottom: '1rem' }}>
            <UserAdmin size={24} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
            Admin
          </h1>
          <p style={{ color: 'var(--cds-text-secondary)', marginBottom: '2rem' }}>
            Manage users, roles, and platform-level operations.
          </p>

          {error && (
            <InlineNotification
              kind="error"
              title="Error"
              subtitle={error}
              onClose={() => setError(null)}
              style={{ marginBottom: '1rem' }}
            />
          )}

          {/* Global consumption summary tiles */}
          {stats && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '1rem',
                marginBottom: '2rem',
              }}
            >
              <SummaryTile icon={<UserAdmin size={20} />} label="Users" value={stats.totals.user_count} />
              <SummaryTile icon={<Document size={20} />} label="Ontologies" value={stats.totals.ontology_count} />
              <SummaryTile icon={<Information size={20} />} label="Nodes" value={stats.totals.node_count} />
              <SummaryTile icon={<ConnectSource size={20} />} label="Edges" value={stats.totals.edge_count} />
              <SummaryTile icon={<ChartBar size={20} />} label="Tokens used" value={stats.totals.total_tokens} />
            </div>
          )}

          {/* ── Embeddings section ─────────────────────────────────────── */}
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: '2.5rem 0 0.5rem' }}>
            <Ai size={20} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
            Embedding Backfill
          </h2>
          <p style={{ color: 'var(--cds-text-secondary)', marginBottom: '1.25rem', fontSize: '0.875rem' }}>
            Re-generate missing embeddings for any layer without re-running the full ingestion pipeline.
            Start with <strong>L2 summaries/captions</strong> (covers image KUs), then <strong>L1 units</strong>
            (short text), and finally <strong>Ontology nodes</strong> if needed.
          </p>

          {embedError && (
            <InlineNotification
              kind="error"
              title="Error"
              subtitle={embedError}
              onClose={() => setEmbedError(null)}
              style={{ marginBottom: '1rem' }}
            />
          )}

          {/* Stats + launch buttons */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
            <EmbedLayerCard
              label="L1 — KU Direct Embeddings"
              description="Short text Knowledge Units embedded directly from content."
              stats={embedStats?.ku_l1 ?? null}
              statsLoading={embedStatsLoading}
              target="ku_l1"
              launching={launching}
              onLaunch={handleStartReembed}
            />
            <EmbedLayerCard
              label="L2 — Summaries & Captions"
              description="AI summaries (long text) and VLM captions (images). Critical for image KU matching."
              stats={embedStats?.ku_l2 ?? null}
              statsLoading={embedStatsLoading}
              target="ku_l2"
              launching={launching}
              onLaunch={handleStartReembed}
            />
            <EmbedLayerCard
              label="Ontology Nodes"
              description="Concept node embeddings used for semantic matching during populate."
              stats={embedStats?.ontology_nodes ?? null}
              statsLoading={embedStatsLoading}
              target="ontology_nodes"
              launching={launching}
              onLaunch={handleStartReembed}
            />
          </div>

          {/* Job history */}
          {embedJobs.length > 0 && (
            <div style={{ marginBottom: '2.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>Recent Jobs</span>
                <button
                  onClick={() => void refreshEmbedJobs()}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--cds-text-secondary)', display: 'flex', alignItems: 'center' }}
                  title="Refresh job list"
                >
                  <Renew size={16} />
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {embedJobs.map((job) => (
                  <ReembedJobRow key={job.id} job={job} />
                ))}
              </div>
            </div>
          )}

          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: '0.5rem 0 1rem' }}>
            <UserAdmin size={20} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
            User Management
          </h2>
          <p style={{ color: 'var(--cds-text-secondary)', marginBottom: '1.5rem', fontSize: '0.875rem' }}>
            Manage application users. A user row appears the first time someone signs in.
            <br />
            <strong>Roles are read-only here.</strong> They come from the Keycloak realm claims
            (<code>realm_access.roles</code>) and are refreshed at each sign-in, so the value shown
            is the one carried by the user&apos;s last login. To change a role, change the realm role
            assignment in Keycloak; nothing in this application can grant it.
          </p>

          {isLoading ? (
            <Loading withOverlay={false} />
          ) : (
            <TableContainer title="Users" description={`${users.length} registered user(s)`}>
              <Table>
                <TableHead>
                  <TableRow>
                    {headers.map(h => (
                      <TableHeader key={h.key}>{h.header}</TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map(row => (
                    <TableRow key={row.id}>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>{row.display_name}</TableCell>
                      <TableCell>
                        <Tag
                          type={getRoleTagType(row.role)}
                          size="sm"
                          title="From the Keycloak realm claims at the last sign-in — not editable here"
                        >
                          {row.role}
                        </Tag>
                      </TableCell>
                      <TableCell>
                        {row.actions.beta_allowed ? (
                          <Tag
                            type={row.actions.beta_features_enabled ? 'teal' : 'cool-gray'}
                            size="sm"
                            title={
                              row.actions.beta_features_enabled
                                ? 'Allowed by admin and enabled by user'
                                : 'Allowed by admin, not enabled by user'
                            }
                          >
                            {row.actions.beta_features_enabled ? 'Allowed · On' : 'Allowed'}
                          </Tag>
                        ) : (
                          <Tag type="gray" size="sm">Not allowed</Tag>
                        )}
                      </TableCell>
                      <TableCell>{fmt.format(row.ontologies)}</TableCell>
                      <TableCell>{fmt.format(row.nodes)}</TableCell>
                      <TableCell>{fmt.format(row.edges)}</TableCell>
                      <TableCell
                        title={
                          row.tokens > 0
                            ? `Prompt: ${row.promptTokens.toLocaleString()} · Completion: ${row.completionTokens.toLocaleString()}`
                            : 'No token usage recorded yet'
                        }
                      >
                        {fmt.format(row.tokens)}
                      </TableCell>
                      <TableCell>{row.last_login_at}</TableCell>
                      <TableCell>
                        <Button
                          kind="ghost"
                          size="sm"
                          onClick={() =>
                            handleToggleBetaAllowed(row.actions, !row.actions.beta_allowed)
                          }
                          style={{ marginRight: '0.5rem' }}
                          title={
                            row.actions.beta_allowed
                              ? 'Revoke beta access (also disables the user opt-in)'
                              : 'Allow this user to opt into beta features'
                          }
                        >
                          {row.actions.beta_allowed ? 'Revoke beta' : 'Allow beta'}
                        </Button>
                        <Button
                          kind="danger--ghost"
                          size="sm"
                          renderIcon={TrashCan}
                          iconDescription="Delete user"
                          onClick={() => setDeleteModal({ open: true, user: row.actions })}
                          hasIconOnly
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </div>
      </Column>

      {/* Delete User Modal */}
      <Modal
        open={deleteModal.open}
        onRequestClose={() => setDeleteModal({ open: false, user: null })}
        onRequestSubmit={handleDeleteUser}
        modalHeading="Delete User"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        danger
      >
        <p>
          Are you sure you want to delete <strong>{deleteModal.user?.email}</strong>?
          This action cannot be undone and will remove all their data.
        </p>
      </Modal>
    </Grid>
  );
}

/** Compact stat tile used in the admin summary row. */
function SummaryTile(props: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <div
      style={{
        background: 'var(--cds-layer)',
        padding: '1rem',
        border: '1px solid var(--cds-border-subtle)',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.25rem',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          color: 'var(--cds-text-secondary)',
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        {props.icon}
        <span>{props.label}</span>
      </div>
      <div style={{ fontSize: '1.75rem', fontWeight: 600 }}>
        {(props.value ?? 0).toLocaleString()}
      </div>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface EmbedLayerCardProps {
  label: string;
  description: string;
  stats: { missing: number; total: number } | null;
  statsLoading: boolean;
  target: ReembedTarget;
  launching: ReembedTarget | null;
  onLaunch: (target: ReembedTarget) => void;
}

function EmbedLayerCard({
  label, description, stats, statsLoading, target, launching, onLaunch,
}: EmbedLayerCardProps) {
  const missing = stats?.missing ?? 0;
  const total = stats?.total ?? 0;
  const present = total - missing;
  const pct = total > 0 ? Math.round((present / total) * 100) : 100;
  const allGood = stats !== null && missing === 0;

  return (
    <div style={{
      background: 'var(--cds-layer)',
      border: `1px solid ${allGood ? 'var(--cds-support-success)' : 'var(--cds-border-subtle)'}`,
      padding: '1rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem',
    }}>
      <div>
        <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.25rem' }}>{label}</div>
        <div style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)' }}>{description}</div>
      </div>

      {statsLoading ? (
        <div style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)' }}>Loading stats…</div>
      ) : stats ? (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
            <span style={{ color: allGood ? 'var(--cds-support-success)' : 'var(--cds-text-secondary)' }}>
              {allGood ? '✓ All embedded' : `${missing.toLocaleString()} missing`}
            </span>
            <span style={{ color: 'var(--cds-text-secondary)' }}>{present.toLocaleString()} / {total.toLocaleString()}</span>
          </div>
          <ProgressBar
            value={pct}
            max={100}
            size="small"
            status={allGood ? 'finished' : 'active'}
            label=""
            hideLabel
          />
        </div>
      ) : (
        <div style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)' }}>Stats unavailable</div>
      )}

      <Button
        kind={allGood ? 'ghost' : 'primary'}
        size="sm"
        renderIcon={PlayFilled}
        disabled={launching !== null}
        onClick={() => onLaunch(target)}
      >
        {launching === target ? 'Starting…' : allGood ? 'Re-run anyway' : 'Backfill now'}
      </Button>
    </div>
  );
}

function ReembedJobRow({ job }: { job: ReembedJob }) {
  const targetLabel: Record<ReembedTarget, string> = {
    ku_l1: 'L1 KU Direct',
    ku_l2: 'L2 Summaries',
    ontology_nodes: 'Ontology Nodes',
  };

  const pct = job.total > 0 ? Math.round(((job.embedded + job.failed) / job.total) * 100) : 0;

  return (
    <div style={{
      background: 'var(--cds-layer)',
      border: '1px solid var(--cds-border-subtle)',
      padding: '0.75rem 1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      fontSize: '0.8125rem',
    }}>
      {/* Status icon */}
      <span style={{ flexShrink: 0 }}>
        {job.status === 'running' && <Loading small withOverlay={false} style={{ width: 16, height: 16 }} />}
        {job.status === 'completed' && <CheckmarkFilled size={16} style={{ color: 'var(--cds-support-success)' }} />}
        {job.status === 'failed' && <ErrorFilled size={16} style={{ color: 'var(--cds-support-error)' }} />}
      </span>

      {/* Target + time */}
      <div style={{ flexShrink: 0, minWidth: 130 }}>
        <div style={{ fontWeight: 600 }}>{targetLabel[job.target]}</div>
        <div style={{ color: 'var(--cds-text-secondary)', fontSize: '0.75rem' }}>
          {new Date(job.startedAt).toLocaleTimeString()}
        </div>
      </div>

      {/* Progress bar (running/completed) */}
      {job.status !== 'failed' && job.total > 0 ? (
        <div style={{ flex: 1 }}>
          <ProgressBar
            value={pct}
            max={100}
            size="small"
            status={job.status === 'completed' ? 'finished' : 'active'}
            label=""
            hideLabel
          />
          <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem', color: 'var(--cds-text-secondary)', marginTop: '0.2rem' }}>
            <span>Embedded: {job.embedded.toLocaleString()}</span>
            {job.failed > 0 && <span style={{ color: 'var(--cds-support-error)' }}>Failed: {job.failed}</span>}
            <span>Total: {job.total.toLocaleString()}</span>
          </div>
        </div>
      ) : (
        <div style={{ flex: 1, color: 'var(--cds-text-secondary)', fontSize: '0.75rem' }}>
          {job.message ?? (job.status === 'running' ? 'Starting…' : '—')}
        </div>
      )}

      {/* Completion time */}
      {job.completedAt && (
        <div style={{ flexShrink: 0, color: 'var(--cds-text-secondary)', fontSize: '0.75rem' }}>
          {new Date(job.completedAt).toLocaleTimeString()}
        </div>
      )}
    </div>
  );
}

// Made with Bob
